import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import * as fromActions from './payment.actions';
import { ErrorHandlerService } from '../../services/error-handler/error-handler.base-service';
import { PaymentRequestsFacade } from '../../services/payment/payment.facade';
import { IPaymentPositionResident } from '../../models';
import { Store } from '@ngrx/store';
import {
  getActivePaymentRequests,
  getActivePaymentRequestsActionState,
  getArchivePaymentsActionState,
  getArchivePaymentsRequests,
} from './payment.selectors';
import { getPermissions } from '../account';
import { differenceInMinutes } from 'date-fns';

@Injectable()
export class PaymentRequestsEffects {
  loadPaymentRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadPaymentRequests),
      withLatestFrom(
        this.store.select(getPermissions),
        this.store.select(getActivePaymentRequests),
        this.store.select(getActivePaymentRequestsActionState)
      ),
      switchMap(([action, permissions, data, actionState]) => {
        // Only call getPaymentRequests when the Payment module is booked; otherwise the
        // backend throws. The booked-modules flag comes from getCustomerAppPermissions and is
        // already loaded up-front by the route resolvers (ART-19480).
        if (!permissions?.payment) {
          return of(fromActions.LoadPaymentRequestsSuccess({ response: [] }));
        }
        const noData = !data || data?.length === 0;
        const doneTimeIsOld =
          actionState?.doneTime && differenceInMinutes(new Date(), actionState.doneTime) > 30;
        if (noData || doneTimeIsOld || action.forced) {
          return this.paymentRequestsFacade.getPaymentsRequests(false).pipe(
            map((response: IPaymentPositionResident[]) =>
              fromActions.LoadPaymentRequestsSuccess({ response })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on payment.effects.ts:loadPaymentRequests$:',
                error
              );
              return [fromActions.LoadPaymentRequestsFailed({ error })];
            })
          );
        } else {
          return of(
            fromActions.LoadPaymentRequestsSuccess({
              response: data,
            })
          );
        }
      })
    )
  );
  loadArchivePaymentRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchivePaymentRequests),
      withLatestFrom(
        this.store.select(getPermissions),
        this.store.select(getArchivePaymentsRequests),
        this.store.select(getArchivePaymentsActionState)
      ),
      switchMap(([action, permissions, data, actionState]) => {
        // Same Payment-module guard as loadPaymentRequests$ (ART-19480).
        if (!permissions?.payment) {
          return of(fromActions.LoadArchivePaymentRequestsSuccess({ response: [] }));
        }
        const noData = !data || data?.length === 0;
        const doneTimeIsOld =
          actionState?.doneTime && differenceInMinutes(new Date(), actionState.doneTime) > 30;
        if (noData || doneTimeIsOld || action.forced) {
          return this.paymentRequestsFacade.getPaymentsRequests(true).pipe(
            map((response: IPaymentPositionResident[]) =>
              fromActions.LoadArchivePaymentRequestsSuccess({ response })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on payment.effects.ts:loadArchivePaymentRequests$:',
                error
              );
              return [fromActions.LoadArchivePaymentRequestsFailed({ error })];
            })
          );
        } else {
          return of(
            fromActions.LoadArchivePaymentRequestsSuccess({
              response: data,
            })
          );
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private paymentRequestsFacade: PaymentRequestsFacade,
    private store: Store,
    private errorHandlerService: ErrorHandlerService
  ) {}
}
